import { React, useEffect, useState } from 'react';
import {
  Link,
  Outlet,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  IconButton,
  Avatar,
  Grid,
  MenuItem,
  Typography,
  Badge,
  CircularProgress,
  Backdrop,
  Button,
  Chip,
  SwipeableDrawer,
  ListItem,
  List,
  ListItemButton,
  ListItemIcon,
  Divider,
} from '@mui/material';
import PropTypes from 'prop-types';
import moment from 'moment';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { ReactComponent as RightDoubleArrowIcon } from '../../../assets/common-assets/rightArrow.svg';
import { ReactComponent as MoonIcon } from '../../../assets/common-assets/moon.svg';
import { ReactComponent as BellIcon } from '../../../assets/common-assets/bell.svg';
import { ReactComponent as FileText } from '../../../assets/common-assets/file-text.svg';
import { ReactComponent as EditDocument } from '../../../assets/common-assets/edit.svg';
import { ReactComponent as FlowConfigurationChange } from '../../../assets/common-assets/flowConfiguration.svg';
import { ReactComponent as CreditsIcon } from '../../../assets/common-assets/dollar-sign.svg';
import { ReactComponent as SubscriptionPlan } from '../../../assets/common-assets/subscription-plan.svg';
import { ReactComponent as HelpAndSupportIcon } from '../../../assets/common-assets/help-circle.svg';
import logoIcon from '../../../assets/common-assets/docu-transcribe-logo.png';
import { ReactComponent as ExternalLinksIcon } from '../../../assets/common-assets/external-link.svg';
import { ReactComponent as AccountInfoIcon } from '../../../assets/common-assets/user.svg';
import { ReactComponent as ReferAFriendIcon } from '../../../assets/common-assets/user-plus.svg';
import { ReactComponent as LogoutIcon } from '../../../assets/navbar-assets/log-out.svg';
import { logoutUser } from '../../../services/userApi';
import { getLoggedInUser } from '../../../store/userSlice';
import { getLocalStorageItem } from '../../../services/localStorageUtils';
import { NAVBAR_TOOLTIP } from '../../../constants/tooltipConstant';
import { completeNotification } from '../../../store/socketSlice';
import {
  fetchNotifications,
  markNotificationAsRead,
} from '../../../services/tabSection';
import ConfirmDialog from '../../pages/utils/ConfirmDialog';
import CustomTooltip from '../../pages/utils/CustomTooltip';
import {
  Root,
  classes,
  ProfileSettingMenu,
  StyledLoginButton,
  StyledSVGIcon,
  MenuItemUserName,
  MenuItemIconButtonUserName,
  BoxUserNameAndRole,
} from './utils';
import { darkPalette, lightPalette } from '../../../theme';
import { getOneDocument } from '../../../services/allInvoiceApi';
import { getTypeofTheme } from '../../../store/themeSlice';
import {
  INVALID_INPUT_MESSAGES,
  INVALID_INPUT_REGEX,
  NAVBAR,
  REFER_A_FRIEND,
} from '../../../constants/staticContent';
import ROLES from '../../../constants/roles';
import { globalClasses } from '../../pages/utils/globalUtils';
import ReferAFriendPopUp from '../../pages/authentication/common/ReferAFriendPopUp';
import { inviteOrganizationRootAdmin } from '../../../services/addOrganizationApi';

const Navbar = ({ openSidebar, setOpenSidebar, screenWidth }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const userCheckLogin = {
    accessToken: getLocalStorageItem('AccessToken'),
    refreshToken: getLocalStorageItem('RefreshToken'),
    role: getLocalStorageItem('role'),
  };

  const getRoleName = (value) => {
    switch (value) {
      case ROLES.SUPER_ADMIN:
        return 'Super Admin';
      case ROLES.ADMIN:
        return 'Admin';
      case ROLES.MANAGER:
        return 'Manager';
      case ROLES.MEMBER:
        return 'Member';
      case ROLES.SUPER_MANAGER:
        return 'Super Manager';
      case ROLES.SALES_ADMIN:
        return 'Sales Admin';
      case ROLES.SALES:
        return 'Sales';
      case ROLES.SUPPORT:
        return 'Support';
      default:
        return '';
    }
  };

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [invoiceNotification, setInvoiceNotification] = useState(null);
  const [userAvatar, setUserAvatar] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNavigateDialogOpen, setIsNavigateDialogOpen] = useState(false);
  const [invisible, setInvisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [openReferAFriendPopup, setOpenReferAFriendPopup] = useState(false);
  const userDetail = useSelector((state) => state.user.user);
  const userId = userDetail?.id;
  const modeType = useSelector((state) => state.theme.type);
  const handleChangeThemeType = () => {
    dispatch(getTypeofTheme(modeType === 'light' ? 'dark' : 'light'));
  };

  const notifyUser = useSelector((state) => state.socket.notification);

  const notificationMessage = notifyUser.message;

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [documentId, setDocumentId] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [askingMessagePopup, setAskingMessagePopup] = useState('');
  const [navigationPathUrl, setNavigationPathUrl] = useState('');
  const [emailError, setEmailError] = useState('');
  const [email, setEmail] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [selectedExpiration, setSelectedExpiration] = useState({
    title: '2 Days',
    time: '2d',
  });
  const [remainingCredits, setRemainingCredits] = useState(0);

  const [openNotifyCenter, setOpenNotifyCenter] = useState(false);

  const toggleDrawer = (isOpen) => (event) => {
    if (
      event &&
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setInvisible(unreadCount === 0 ? !invisible : invisible);
    setOpenNotifyCenter(isOpen);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };
  const handleCloseNavigateDialog = () => {
    setIsNavigateDialogOpen(false);
  };
  useEffect(() => {
    if (userDetail?.organization) {
      setRemainingCredits(userDetail.organization?.remaining_credits);
    }
  }, [userDetail?.organization]);
  useEffect(() => {
    if (notificationMessage) {
      const credtits = notifyUser?.deducted_credits || 0;
      setRemainingCredits((prev) => prev - credtits);
    }
  }, [notificationMessage]);
  const toggleDrawerNav = (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setOpenSidebar((prev) => {
      // Only allow `false` if `screenWidth` is greater than 600
      if (!prev && screenWidth < 600) {
        console.log('##################33');

        return true; // Keep it `true` if `screenWidth <= 600`
      }
      return !prev; // Toggle otherwise
    });
  };

  const getDocumentById = async (id) => {
    const Document = await getOneDocument(id);
    if (Document) {
      const fileName = Document?.data?.data?.name;
      setDocumentName(fileName);
    }
  };

  const handleConfirmAction = async () => {
    setIsLoading(true);
    setIsDialogOpen(false);
    setAnchorElUser(null);
    const logoutHandle = await logoutUser();
    if (logoutHandle) {
      dispatch(getLoggedInUser({}));

      setIsLoading(false);
      navigate('/login');
    } else {
      navigate('/login');
    }
  };

  const handleNavigateConfirmAction = async () => {
    setIsNavigateDialogOpen(false);
  };
  const handleBackToHome = () => {
    if (
      userCheckLogin.role === ROLES.SUPER_ADMIN ||
      userCheckLogin.role === ROLES.SUPER_MANAGER
    ) {
      navigate('/dashboard');
    } else if (userCheckLogin.role === ROLES.SALES_ADMIN) {
      navigate('/sales-admin-dashboard');
    } else if (userCheckLogin.role === ROLES.SALES) {
      navigate('/subscriber');
    } else if (params?.version !== undefined) {
      if (location.pathname.includes('invoice')) {
        navigate(`invoices/versions/${params.id}`);
      } else if (location.pathname.includes('receipt')) {
        navigate(`receipts/versions/${params.id}`);
      } else if (location.pathname.includes('sale')) {
        navigate(`sales/versions/${params.id}`);
      } else if (location.pathname.includes('bank-statement')) {
        navigate(`bank-statements/versions/${params.id}`);
      } else if (location.pathname.includes('creditcardstatement')) {
        navigate(`creditcardstatements/versions/${params.id}`);
      }
    } else {
      navigate(-1);
    }
  };

  // Fetch notifications from the backend API
  const fetchNotificationsData = async () => {
    if (
      userCheckLogin.role !== ROLES.SUPER_ADMIN &&
      userCheckLogin.role !== ROLES.SUPER_MANAGER &&
      userCheckLogin.role !== ROLES.SALES_ADMIN &&
      userCheckLogin.role !== ROLES.SALES &&
      !location.pathname.includes('notifications')
    ) {
      const allNotification = await fetchNotifications();
      const filterNotification = allNotification?.notifications?.filter(
        (notification) =>
          notification.document !== null ||
          notification.flow !== null ||
          notification.payment !== null ||
          notification.OrganizationSubscription !== null,
      );
      setNotifications(filterNotification);
    }
  };

  const getUnreadCount = (notificationsUnread) =>
    notificationsUnread?.reduce(
      (count, notification) =>
        notification.status === 'unread' ? count + 1 : count,
      0,
    );
  useEffect(() => {
    if (userId) {
      fetchNotificationsData();
    }
  }, [notificationMessage, userDetail]);

  useEffect(() => {
    const updateUnreadCount = () => {
      setUnreadCount(getUnreadCount(notifications));
      dispatch(completeNotification({}));
    };

    updateUnreadCount();
  }, [notifications]);

  const getDocumentTypeNav = (value) => {
    switch (value) {
      case 'BS':
        return 'bank-statement';
      case 'CC':
        return 'creditcardstatement';
      case 'sales':
        return 'sale';
      case 'invoice':
        return 'invoice';
      case 'receipt':
        return 'receipt';
      default:
        return '';
    }
  };

  const getDocumentTypeMainNavigation = (value) => {
    switch (value) {
      case 'BS':
        return 'bank-statements';
      case 'CC':
        return 'creditcardstatements';
      case 'sales':
        return 'sales';
      case 'invoice':
        return 'invoices';
      case 'receipt':
        return 'receipts';
      default:
        return '';
    }
  };
  const handleNavigationForNotification = async (notificationData) => {
    setDocumentName(notificationData?.document?.name);
    if (
      notificationData?.document &&
      notificationData?.title === 'Document Status'
    ) {
      setIsNavigateDialogOpen(true);
      if (notificationData?.document?.status === 'Completed') {
        setAskingMessagePopup('Invoice data parsing is completed.');
        setNavigationPathUrl(
          `/${getDocumentTypeNav(notificationData?.document?.document_type)}/${notificationData?.invoice_id}`,
        );
      } else {
        setAskingMessagePopup('Invoice data parsing is failed.');
        setNavigationPathUrl(
          `/${getDocumentTypeMainNavigation(notificationData?.document?.document_type)}`,
        );
      }
    } else if (
      notificationData?.document &&
      notificationData?.title === 'Document Change'
    ) {
      setIsNavigateDialogOpen(true);
      if (notificationData?.document?.status === 'Completed') {
        setAskingMessagePopup(notificationData?.message);
        setNavigationPathUrl(
          `/${getDocumentTypeNav(notificationData?.document?.document_type)}/${notificationData?.invoice_id}`,
        );
      } else {
        setAskingMessagePopup('');
      }
    } else if (
      notificationData?.flow &&
      notificationData?.title === 'Flow Change'
    ) {
      setIsNavigateDialogOpen(true);
      setAskingMessagePopup(notificationData?.message);
      setNavigationPathUrl(
        `/flow-configuration/editor?flowId=${notificationData?.flow_id}`,
      );
    } else if (
      notificationData?.OrganizationSubscription &&
      notificationData?.title === 'Plan Expiry'
    ) {
      setIsNavigateDialogOpen(true);
      setAskingMessagePopup(notificationData?.message);
      setNavigationPathUrl('/account-info?tab=billingInfo');
    } else if (
      notificationData?.payment &&
      notificationData?.title === 'Credits'
    ) {
      setIsNavigateDialogOpen(true);
      setAskingMessagePopup(notificationData?.message);
      setNavigationPathUrl('/account-info?tab=myAccount');
    }

    setInvoiceNotification(notificationData);
  };

  const markAsRead = async (notificationData) => {
    if (userCheckLogin.role !== ROLES.SUPER_ADMIN) {
      setIsLoading(true);
      await markNotificationAsRead(notificationData?.id);
      setIsLoading(false);
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) => {
          if (notification.id === notificationData?.id) {
            return { ...notification, status: 'read' };
          }
          return notification;
        }),
      );
      setUnreadCount((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
    }

    await handleNavigationForNotification(notificationData);
  };

  // Sort notifications by 'created_at' in ascending order
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleAccountMenu = () => {
    if (
      userCheckLogin.role === ROLES.SUPER_ADMIN ||
      userCheckLogin.role === ROLES.SUPER_MANAGER ||
      userCheckLogin.role === ROLES.SALES_ADMIN ||
      userCheckLogin.role === ROLES.SALES
    ) {
      navigate('/my-account');
    } else {
      navigate('/account-info?tab=myAccount');
    }
    setAnchorElUser(null);
  };

  const handleReferAFriendMenu = () => {
    setOpenReferAFriendPopup(true);
  };
  const handleReferAFriendPopupClose = () => {
    setOpenReferAFriendPopup(false);
    setEmailError('');
    setEmail('');
    setSelectedExpiration({
      title: '2 Days',
      time: '2d',
    });
  };

  const handleLogoutMenu = async () => {
    if (
      userCheckLogin &&
      userCheckLogin?.role !== undefined &&
      userCheckLogin?.accessToken !== undefined &&
      userCheckLogin?.refreshToken !== undefined
    ) {
      setIsDialogOpen(true);
    }
  };

  const handleNavigateLoginNavbar = () => {
    if (
      userCheckLogin.role === undefined &&
      userCheckLogin.accessToken === undefined &&
      userCheckLogin.refreshToken === undefined
    ) {
      return navigate('/login');
    }
  };

  const fetchData = async () => {
    const role = getLocalStorageItem('role');
    if (role) {
      setUserAvatar(userDetail?.avatar);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userDetail]);

  useEffect(() => {
    setDocumentId(params?.id);
  }, [params?.id]);

  useEffect(() => {
    const invoiceRegex = /^\/invoice(\/([^/]+))?\/([^/]+)$/;
    const receiptRegex = /^\/receipt(\/([^/]+))?\/([^/]+)$/;
    const saleRegex = /^\/sale(\/([^/]+))?\/([^/]+)$/;
    const ccRegex = /^\/credit-card-statemnt(\/([^/]+))?\/([^/]+)$/;

    if (
      (location.pathname.match(invoiceRegex) ||
        location.pathname.match(receiptRegex) ||
        location.pathname.match(saleRegex) ||
        location.pathname.match(ccRegex)) &&
      documentId
    ) {
      getDocumentById(documentId);
    }
  }, [documentId, location.pathname, params?.version]);

  const settings = [
    // {
    //   id: 1,
    //   menu: 'My Account',
    //   icon: (
    //     <StyledSVGIcon
    //       component={AccountInfoIcon}
    //       stroke={lightPalette.lunarBase.main}
    //     />
    //   ),
    //   function: handleAccountMenu,
    // },
    ...(userCheckLogin.role === ROLES.ADMIN
      ? [
          {
            id: 1,
            menu: 'Refer a Friend',
            icon: (
              <StyledSVGIcon
                component={ReferAFriendIcon}
                stroke={lightPalette.lunarBase.main}
              />
            ),
            function: handleReferAFriendMenu,
          },
        ]
      : []),
    {
      id: 2,
      menu: 'Logout',
      icon: (
        <StyledSVGIcon
          component={LogoutIcon}
          stroke={lightPalette.lunarBase.main}
        />
      ),
      function: handleLogoutMenu,
    },
  ];

  const calculateTimeAgo = (timestamp) => {
    const notificationTime = moment(timestamp);
    const currentTime = moment();
    const diff = currentTime.diff(notificationTime, 'minutes'); // Calculate the difference in minutes

    if (diff < 1) {
      return 'now';
    }
    if (diff < 60) {
      return `${diff}m `;
    }
    if (diff < 1440) {
      return `${Math.floor(diff / 60)}h`;
    }
    if (diff < 10080) {
      return `${Math.floor(diff / 1440)}d`;
    }
    return `${Math.floor(diff / 10080)}w`;
  };

  function getRedirectPath(loggedInUser) {
    if (
      loggedInUser?.role === ROLES.SUPER_ADMIN ||
      loggedInUser?.role === ROLES.SUPER_MANAGER
    ) {
      return '/dashboard';
    }
    if (loggedInUser?.role === ROLES.SUPER_MANAGER) {
      return '/sales-admin-dashboard';
    }
    return '/invoices';
  }

  const iconToShow = openSidebar ? (
    <MenuIcon />
  ) : (
    <KeyboardDoubleArrowLeftIcon />
  );

  useEffect(() => {
    if (userDetail?.referral_code && userDetail?.referral_code !== '') {
      setReferralCode(userDetail?.referral_code);
    }
  }, [userDetail]);
  const emailRegex = INVALID_INPUT_REGEX.EMAIL;
  const isFirstLatterCapital = INVALID_INPUT_REGEX.EMAIL_CAPITAL.test(email);
  const sendEmailHandler = async () => {
    if (!email || email.trim() === '') {
      setEmailError(REFER_A_FRIEND.EMAIL_REQUIRED);
      return;
    }
    if (!emailRegex.test(email) || !isFirstLatterCapital) {
      setEmailError(INVALID_INPUT_MESSAGES.EMAIL);
      return;
    }
    setIsButtonLoading(true);
    const expiryTime = selectedExpiration?.time;
    const organization = '';
    const response = await inviteOrganizationRootAdmin({
      invitationEmail: email,
      expiryTime,
      organization,
      referredBy: referralCode,
    });
    setIsButtonLoading(false);
    if (response) {
      handleReferAFriendPopupClose();
    }
    setEmailError('');
    handleReferAFriendPopupClose();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'Document Status':
        return <FileText />;
      case 'Document Change':
        return <EditDocument />;
      case 'Flow Change':
        return <FlowConfigurationChange />;
      case 'Credits':
        return <CreditsIcon />;
      case 'Plan Expiry':
        return <SubscriptionPlan />;
      default:
        return <BellIcon />;
    }
  };
  return (
    <>
      <Root>
        {isLoading ? (
          <Backdrop className={classes.navbarLoader} open={isLoading}>
            <CircularProgress color="inherit" />
          </Backdrop>
        ) : null}
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          className={classes.navbarContainerGrid}
        >
          <Grid item xs={7} lg={9} className={classes.navbarLeftGrid}>
            <Grid container alignItems="center" height="100%">
              {![
                '/invoice/',
                '/bank-statement/',
                '/creditcardstatement/',
                '/receipt/',
                '/sale/',
              ].some((substring) => location?.pathname.includes(substring)) ? (
                <Grid item xs={2} sm={1.5} md={1} lg={0.5}>
                  <CustomTooltip title={NAVBAR_TOOLTIP.TOGGLE_BUTTON}>
                    <Button
                      onClick={toggleDrawerNav}
                      className={classes.sidebarToggleButtonFromNav}
                    >
                      {iconToShow}
                    </Button>
                  </CustomTooltip>
                </Grid>
              ) : null}

              <Grid item xs={10} sm={10.5} md={11} lg={11.5}>
                {![
                  '/invoice/',
                  '/bank-statement/',
                  '/creditcardstatement/',
                  '/receipt/',
                  '/sale/',
                ].some((substring) =>
                  location?.pathname.includes(substring),
                ) ? (
                  <Link
                    className={globalClasses.displayFlex}
                    to={() => getRedirectPath(userCheckLogin)}
                  >
                    <Box
                      className={classes.logoImage}
                      component="img"
                      src={logoIcon}
                      alt="logoIcon"
                    />
                  </Link>
                ) : (
                  <Box className={classes.annotationDocumentNameBox}>
                    <IconButton
                      onClick={handleBackToHome}
                      className={classes.IconButtonBackHome}
                    >
                      <ArrowBackOutlinedIcon />
                    </IconButton>
                    <Typography
                      variant="body1"
                      color={lightPalette.secondaryWhite.main}
                    >
                      {documentName}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={5} lg={3} className={classes.navbarRightGrid}>
            <Box className={classes.rightMainBox}>
              <Box className={classes.rightMainEndMenuBox}>
                {userCheckLogin?.role === ROLES.ADMIN ||
                userCheckLogin?.role === ROLES.MANAGER ||
                userCheckLogin?.role === ROLES.MEMBER
                  ? userDetail &&
                    userDetail?.organization && (
                      <Chip
                        className={classes.chipBackGround}
                        label={
                          <Box display="flex" alignItems="center">
                            <Typography
                              variant="body"
                              fontWeight="600"
                              color={darkPalette.secondaryWhite.main}
                            >
                              {`Remaining Credit: ${userDetail && remainingCredits}`}
                            </Typography>
                          </Box>
                        }
                      />
                    )
                  : null}

                {userCheckLogin?.role === ROLES.SUPER_ADMIN ||
                userCheckLogin?.role === ROLES.SUPER_MANAGER ||
                userCheckLogin?.role === ROLES.SALES_ADMIN ||
                userCheckLogin?.role === ROLES.SALES
                  ? null
                  : !location.pathname.includes('notifications') && (
                      <CustomTooltip title={NAVBAR_TOOLTIP.NOTIFICATION}>
                        <IconButton
                          className={classes.navbarBoxIconsButtons}
                          onClick={toggleDrawer(true)}
                        >
                          <Badge
                            badgeContent={unreadCount}
                            invisible={invisible}
                            className={classes.navbarBoxIconsButtons}
                          >
                            <BellIcon className={classes.navbarBoxIcons} />
                          </Badge>
                        </IconButton>
                      </CustomTooltip>
                    )}

                <CustomTooltip title={NAVBAR_TOOLTIP.HELP}>
                  <span>
                    <Link
                      to={
                        userCheckLogin.role === ROLES.SUPER_ADMIN ||
                        userCheckLogin.role === ROLES.SUPER_MANAGER ||
                        userCheckLogin.role === ROLES.SALES_ADMIN ||
                        userCheckLogin.role === ROLES.SALES
                          ? '/SA-support'
                          : '/support'
                      }
                    >
                      <IconButton className={classes.navbarBoxIconsButtons}>
                        <HelpAndSupportIcon
                          className={classes.navbarBoxIcons}
                        />
                      </IconButton>
                    </Link>
                  </span>
                </CustomTooltip>

                <CustomTooltip
                  title={
                    modeType === 'light'
                      ? NAVBAR_TOOLTIP.DARK
                      : NAVBAR_TOOLTIP.LIGHT
                  }
                >
                  <span>
                    <IconButton
                      className={classes.navbarBoxIconsButtons}
                      onClick={handleChangeThemeType}
                    >
                      {modeType === 'light' ? (
                        <MoonIcon className={classes.navbarBoxIcons} />
                      ) : (
                        <LightModeOutlinedIcon
                          className={classes.navbarBoxIcons}
                        />
                      )}
                    </IconButton>
                  </span>
                </CustomTooltip>
                {userCheckLogin.role !== undefined &&
                userCheckLogin.accessToken !== undefined &&
                userCheckLogin.refreshToken !== undefined ? (
                  <CustomTooltip title={NAVBAR_TOOLTIP.PROFILE}>
                    <IconButton
                      className={classes.navbarBoxIconsButtons}
                      onClick={handleOpenUserMenu}
                    >
                      <Avatar className={classes.avatar} src={userAvatar} />
                    </IconButton>
                  </CustomTooltip>
                ) : (
                  <StyledLoginButton onClick={handleNavigateLoginNavbar}>
                    Log In
                  </StyledLoginButton>
                )}
                <ProfileSettingMenu
                  id="menu-appBar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItemUserName key="UserName" onClick={handleAccountMenu}>
                    <MenuItemIconButtonUserName>
                      <StyledSVGIcon
                        component={AccountInfoIcon}
                        stroke={lightPalette.lunarBase.main}
                      />
                    </MenuItemIconButtonUserName>
                    <BoxUserNameAndRole>
                      <Typography
                        variant="body2"
                        textAlign="center"
                        fontWeight="500"
                        color={lightPalette.lunarBase.main}
                      >
                        {userDetail?.first_name} {userDetail?.last_name}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        fontWeight="500"
                        color={lightPalette.lunarBase.main}
                      >
                        {getRoleName(userCheckLogin?.role)}
                      </Typography>
                    </BoxUserNameAndRole>
                  </MenuItemUserName>
                  {settings?.map((setting) => (
                    <MenuItem key={setting.id} onClick={setting.function}>
                      <IconButton>{setting.icon}</IconButton>
                      <Typography
                        variant="body2"
                        textAlign="center"
                        fontWeight="500"
                        color={lightPalette.lunarBase.main}
                      >
                        {setting.menu}
                      </Typography>
                    </MenuItem>
                  ))}
                </ProfileSettingMenu>
              </Box>
            </Box>
          </Grid>
        </Grid>
        {isDialogOpen ? (
          <ConfirmDialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            onConfirm={handleConfirmAction}
            askingMessage={NAVBAR.ASKING_MESSAGE_OF_LOGOUT}
          />
        ) : null}
        {isNavigateDialogOpen ? (
          <ConfirmDialog
            open={isNavigateDialogOpen}
            onClose={handleCloseNavigateDialog}
            onConfirm={handleNavigateConfirmAction}
            askingMessage={askingMessagePopup}
            navigationPath={navigationPathUrl}
            notificationList={invoiceNotification}
          />
        ) : null}
        <ReferAFriendPopUp
          open={openReferAFriendPopup}
          handleClose={handleReferAFriendPopupClose}
          setEmailError={setEmailError}
          emailError={emailError}
          setEmail={setEmail}
          email={email}
          selectedExpiration={selectedExpiration}
          setSelectedExpiration={setSelectedExpiration}
          sendEmailHandler={sendEmailHandler}
          referralCode={referralCode}
          isButtonLoading={isButtonLoading}
        />
        <SwipeableDrawer
          anchor="right"
          open={openNotifyCenter}
          onClose={toggleDrawer(false)}
          onOpen={toggleDrawer(true)}
        >
          <Box sx={{ width: 450 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                height: '48px',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                borderBottom: '2px solid rgba(195, 195, 195, 1)',
                backgroundColor: lightPalette.deepAmethyst.lightest,
              }}
            >
              <CustomTooltip title="Close">
                <IconButton
                  sx={{
                    padding: '10px',
                    borderRadius: 0,
                  }}
                  className={classes.navbarBoxIconsButtons}
                  onClick={toggleDrawer(false)}
                >
                  <RightDoubleArrowIcon
                    className={classes.navbarBoxIcons}
                    stroke={lightPalette.deepAmethyst.main}
                  />
                </IconButton>
              </CustomTooltip>
              <Typography variant="h6" color={lightPalette.deepAmethyst.main}>
                Notifications
              </Typography>
              <CustomTooltip title="View all">
                <IconButton
                  sx={{
                    justifyContent: 'flex-end',
                    padding: '10px',
                    borderRadius: 0,
                  }}
                  className={classes.navbarBoxIconsButtons}
                  onClick={() => {
                    toggleDrawer(false);
                    navigate('/notifications');
                  }}
                >
                  <ExternalLinksIcon
                    className={classes.navbarBoxIcons}
                    stroke={lightPalette.deepAmethyst.main}
                  />
                </IconButton>
              </CustomTooltip>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 'calc(100vh - 48px)',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  borderRadius: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: `${lightPalette.lynxWhite.main}`,
                  borderRadius: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: `${lightPalette.secondaryBlack.lighter}`,
                  borderRadius: '8px',
                },
              }}
              onClick={toggleDrawer(false)}
              onKeyDown={toggleDrawer(false)}
            >
              <List>
                {notifications?.length > 0 ? (
                  notifications?.slice(0, 25).map((item) => (
                    <>
                      <ListItem
                        key={item?.id}
                        disablePadding
                        onClick={
                          item?.status === 'unread'
                            ? () => {
                                markAsRead(item);
                              }
                            : () => {
                                handleNavigationForNotification(item);
                              }
                        }
                      >
                        <ListItemButton
                          sx={{
                            width: '100%',
                            height: '64px',
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              width: '10%',
                            }}
                          >
                            {getNotificationIcon(item?.title)}
                          </ListItemIcon>

                          <Box
                            sx={{
                              width: '80%',
                            }}
                          >
                            <Typography
                              variant="h5"
                              sx={{
                                color:
                                  item?.status === 'unread'
                                    ? lightPalette.primary.main
                                    : lightPalette.secondaryBlack.medium,
                                marginBottom: '4px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {item?.title || ''}
                            </Typography>
                            <CustomTooltip title={item?.message || ''}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  color: lightPalette.secondaryBlack.medium,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {item?.message || ''}
                              </Typography>
                            </CustomTooltip>
                          </Box>
                          <Box
                            sx={{
                              width: '10%',
                              padding: '4px',
                            }}
                          >
                            <Typography
                              variant="body1"
                              color={lightPalette.carbonFiber.medium}
                            >
                              {calculateTimeAgo(item?.created_at)}
                            </Typography>
                          </Box>
                        </ListItemButton>
                      </ListItem>
                      <Divider />
                    </>
                  ))
                ) : (
                  <Typography variant="h5" textAlign="center">
                    No more notifications
                  </Typography>
                )}
              </List>
            </Box>
          </Box>
        </SwipeableDrawer>
      </Root>
      <Outlet />
    </>
  );
};
Navbar.defaultProps = {
  openSidebar: false,
  setOpenSidebar: null,
};

Navbar.propTypes = {
  openSidebar: PropTypes.bool,
  setOpenSidebar: PropTypes.func,
  screenWidth: PropTypes.number.isRequired,
};

export default Navbar;
